import {
  FocusEvent,
  KeyboardEvent,
  FunctionComponent,
  forwardRef,
  PropsWithoutRef,
  Ref,
  useCallback,
  useEffect,
  ChangeEvent
} from 'react';

import { useConsolidatedRef } from '../../../hooks';
import { ForwardProps, OmitStrict } from '../../../types';
import { InputProps } from '../../Input/Input';

import { BareInput } from './DateTime.styles';

export interface PartInputProps extends OmitStrict<InputProps, 'defaultValue' | 'value'> {
  value?: number;
  min?: number;
  max?: number;
  maxLength?: number;
  size?: number;
  onChange?: (value?: string) => void;
}

const PartInput: FunctionComponent<PartInputProps & ForwardProps> = forwardRef(
  (
    {
      value,
      onChange,
      readOnly,
      min = 0,
      max = Infinity,
      size = 2,
      maxLength = Infinity,
      ...restProps
    }: PropsWithoutRef<PartInputProps>,
    ref: Ref<HTMLInputElement>
  ) => {
    const onKeyDown = useCallback((ev: KeyboardEvent<HTMLInputElement>) => {
      if (['+', '-', 'e', 'E', '.', ','].includes(ev.key)) ev.preventDefault();
    }, []);

    const inputRef = useConsolidatedRef(ref);
    useEffect(() => {
      if (inputRef.current && !Number.isNaN(value)) {
        inputRef.current.value = value?.toString().padStart(2, '0') ?? '';
      }
    }, [value, inputRef.current]);

    const onInputChange = useCallback(
      (ev: ChangeEvent<HTMLInputElement>) => {
        const targetEl = ev.target;
        if (targetEl.value.length > maxLength) targetEl.value = targetEl.value.slice(0, maxLength);
        else if (size !== maxLength)
          targetEl.style.width = `${Math.max(targetEl.value.length, size)}ch`;
      },
      [maxLength]
    );

    const onBlur = useCallback(
      (ev: FocusEvent<HTMLInputElement>) => {
        const targetEl = ev.target;
        if (targetEl.value !== '') targetEl.value = targetEl.value.padStart(2, '0');
        onChange?.(targetEl.value);
      },
      [onChange]
    );

    const defaultValue = value ? value.toString().padStart(2, '0') : '';

    return (
      <BareInput
        type='number'
        defaultValue={defaultValue}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onChange={onInputChange}
        step={1}
        min={min}
        max={max}
        maxLength={maxLength}
        readOnly={readOnly}
        ref={inputRef}
        size={size}
        {...restProps}
      />
    );
  }
);

export default PartInput;
